<template>
  <div class="order-panel">
    <div class="top-bar">
      <div class="top-left">
        <a-button size="small" icon="arrow-left" @click="handleBack">返回</a-button>
        <span class="top-table">{{ tableLabel }}</span>
        <template v-if="order">
          <span class="top-order-no">{{ order.order_no }}</span>
          <a-tag :color="statusColor(order.status)">{{ order.statusText || statusText(order.status) }}</a-tag>
        </template>
        <a-tag v-else color="orange">开台中</a-tag>
      </div>
      <div class="top-right">
        <span v-if="storeName" class="top-store">{{ storeName }}</span>
        <span class="top-user">{{ userName }}</span>
        <a-button size="small" :disabled="!parkable" @click="handlePark">挂单</a-button>
        <a-button size="small" type="primary" :disabled="!settleable" @click="handleSettle">结账</a-button>
      </div>
    </div>
    <div class="panel-body">
      <!-- 左：分类 + 菜品（GET /goods/menu：分类树 + 售卖菜品含选项组/估清状态） -->
      <div class="menu-side">
        <div class="cat-list">
          <div class="cat-item" :class="{ active: activeCategory === 0 }" @click="switchCategory(0)">全部</div>
          <div class="cat-item" v-for="c in flatCategories" :key="c.id"
               :class="{ active: activeCategory === c.id }"
               :style="{ paddingLeft: (14 + c.level * 12) + 'px' }"
               @click="switchCategory(c.id)">{{ c.name }}</div>
        </div>
      </div>
      <div class="goods-area">
        <div class="goods-search">
          <a-input-search v-model="searchKey" placeholder="搜索菜品名称" allowClear
                          @search="loadMenu" />
        </div>
        <div class="goods-scroll" v-if="goods.length">
          <div class="goods-card" v-for="g in goods" :key="g.id"
               :class="{ soldout: isSoldOut(g) }" @click="onGoodsClick(g)">
            <div class="goods-img">
              <img v-if="g.imgName" :src="imgUrl(g.imgName)" alt="" />
              <span v-else class="img-holder">{{ g.materialName ? g.materialName[0] : '菜' }}</span>
            </div>
            <div class="goods-info">
              <div class="goods-name">{{ g.materialName }}</div>
              <div class="goods-desc" v-if="g.description">{{ g.description }}</div>
              <div class="goods-bottom">
                <span class="goods-price">¥{{ fmt(g.minPrice) }}</span>
                <a-tag v-if="isSoldOut(g)" color="red">估清</a-tag>
                <span v-else-if="(g.options || []).length" class="goods-opt">可选规格</span>
              </div>
            </div>
          </div>
        </div>
        <div class="goods-empty" v-else-if="!menuLoading">该分类暂无在售菜品</div>
      </div>
      <!-- 右：已点清单（已下单 normal/added + 待下单购物车） -->
      <div class="cart-side">
        <div class="cart-title">已点清单</div>
        <div class="cart-scroll">
          <template v-if="orderItems.length">
            <div class="cart-group-label">已下单（金额为后端计价）</div>
            <div class="cart-line saved" v-for="it in orderItems" :key="it.id">
              <div class="line-main">
                <div class="line-name">
                  {{ it.material_name }}
                  <a-tag v-if="it.item_status === 'added'" color="orange">加菜</a-tag>
                </div>
                <div class="line-sub" v-if="optionsText(it.options_json)">{{ optionsText(it.options_json) }}</div>
                <div class="line-sub" v-if="it.remark">备注：{{ it.remark }}</div>
              </div>
              <div class="line-right">
                <div class="line-qty">×{{ it.quantity }}</div>
                <div class="line-amount">¥{{ fmt(it.all_price) }}</div>
              </div>
            </div>
          </template>
          <template v-if="cart.length">
            <div class="cart-group-label">待下单（价格为参考价）</div>
            <div class="cart-line" v-for="(line, idx) in cart" :key="line.key">
              <div class="line-main">
                <div class="line-name">{{ line.materialName }}</div>
                <div class="line-sub" v-if="line.optionText">{{ line.optionText }}</div>
                <div class="line-sub" v-if="line.remark">备注：{{ line.remark }}</div>
                <div class="qty-step">
                  <a-button size="small" @click="decQty(idx)">-</a-button>
                  <span class="qty-num">{{ line.quantity }}</span>
                  <a-button size="small" @click="line.quantity++">+</a-button>
                  <a-button size="small" type="danger" icon="close" class="line-del" @click="cart.splice(idx, 1)" />
                </div>
              </div>
              <div class="line-right">
                <div class="line-amount">¥{{ fmt(line.unitRef * line.quantity) }}</div>
              </div>
            </div>
          </template>
          <div class="cart-empty" v-if="!orderItems.length && !cart.length">尚未点餐，点击左侧菜品开始点餐</div>
        </div>
        <div class="cart-footer">
          <div class="total-row">
            <span>已下单：</span><span class="total-num">¥{{ fmt(savedTotal) }}</span>
          </div>
          <div class="total-row" v-if="cart.length">
            <span>待下单（参考）：</span><span class="total-num">¥{{ fmt(cartTotal) }}</span>
          </div>
          <a-button type="primary" size="large" block :loading="submitting"
                    :disabled="!canSubmit" @click="handleSubmit">下单</a-button>
        </div>
      </div>
    </div>
    <GoodsOptionsModal :visible="modalVisible" :goods="modalGoods"
                       @confirm="onModalConfirm" @cancel="modalVisible = false" />
  </div>
</template>

<script>
import Vue from 'vue'
import { USER_INFO } from '@/store/mutation-types'
import { getFileAccessHttpUrl } from '@/api/manage'
import { storeMy, menuList, orderDetail, orderCreate, orderAddItems, orderPark } from '@/api/plugin/dining'
import GoodsOptionsModal from './GoodsOptionsModal'

/**
 * 点餐面板（/cashier/dining/order/:tableId，tableId=0 表示外带/无桌台，orderId/storeId 经 query 透传）：
 * 左分类+菜品（估清禁点）→ 选项弹窗（规格/做法单选、加料多选加价、必选校验）→ 右已点清单；
 * 下单=POST /order/{id}/items（无订单先开台）；挂单=park 后回总览；结账=跳 Settle。
 * 已下单金额展示后端 unit_price/all_price；待下单仅展示参考价，成交价一律后端计算。
 */
export default {
  name: 'DiningCashierOrderPanel',
  components: { GoodsOptionsModal },
  data() {
    return {
      userName: '',
      storeName: '',
      storeId: null,
      tableId: 0,
      orderId: null,
      order: null,
      categories: [],
      goods: [],
      activeCategory: 0,
      searchKey: '',
      menuLoading: false,
      cart: [],
      modalVisible: false,
      modalGoods: null,
      submitting: false
    }
  },
  computed: {
    tableLabel() {
      if (this.order) {
        return this.order.order_type === 'takeout' ? '外带单' : (this.order.tableName || '堂食')
      }
      return this.tableId > 0 ? '堂食开台' : '外带单'
    },
    flatCategories() {
      const list = []
      const walk = (nodes, level) => {
        (nodes || []).forEach(n => {
          list.push({ id: n.id, name: n.name, level: level })
          if (n.children && n.children.length) walk(n.children, level + 1)
        })
      }
      walk(this.categories, 0)
      return list
    },
    orderItems() {
      return (this.order && this.order.items) || []
    },
    savedTotal() {
      return this.orderItems.reduce((sum, it) => sum + Number(it.all_price || 0), 0)
    },
    cartTotal() {
      return this.cart.reduce((sum, line) => sum + line.unitRef * line.quantity, 0)
    },
    canSubmit() {
      // 云贝数字状态（Task 3.2）：'1' 开台未下单/待付款/待接单、'2' 就餐中 可下单/加菜
      const status = this.order ? this.order.status : '1'
      return this.cart.length > 0 && (status === '1' || status === '2')
    },
    parkable() {
      // 挂单仅未支付已下单（'1' 且有明细）；'2' 就餐中/已付款不可挂
      return !this.cart.length && !!this.order && this.order.status === '1' && this.orderItems.length > 0
    },
    settleable() {
      // 结账：'1' 已下单（待接单/待付款）/ '2' 就餐中（餐后付）/ parked 均可结算（后端 settleable 同口径）
      return !this.cart.length && !!this.order &&
        ['1', '2', 'parked'].indexOf(this.order.status) > -1 && this.orderItems.length > 0
    }
  },
  created() {
    this.initUserInfo()
    this.tableId = Number(this.$route.params.tableId) || 0
    this.orderId = Number(this.$route.query.orderId) || null
    this.resolveStore()
  },
  methods: {
    initUserInfo() {
      const info = Vue.ls.get(USER_INFO)
      this.userName = (info && (info.username || info.loginName)) || ''
    },
    // 门店上下文：query.storeId 透传优先，否则 sessionStorage('diningStore')，storeMy 兜底
    resolveStore() {
      const fromQuery = Number(this.$route.query.storeId) || null
      let store = null
      try {
        store = JSON.parse(sessionStorage.getItem('diningStore') || 'null')
      } catch (e) {
        store = null
      }
      const target = fromQuery || (store && store.id) || null
      if (target) {
        this.storeId = target
        this.storeName = (store && store.id === target && store.name) || ''
        this.init()
        return
      }
      storeMy().then(res => {
        if (res.code === 200 && res.data && res.data.id) {
          sessionStorage.setItem('diningStore', JSON.stringify(res.data))
          this.storeId = res.data.id
          this.storeName = res.data.name || ''
          this.init()
        } else {
          this.$message.error('未找到所属门店，请重新登录')
          this.$router.push('/cashier/login')
        }
      }).catch(() => {
        this.$message.error('获取门店信息失败，请重新登录')
        this.$router.push('/cashier/login')
      })
    },
    init() {
      this.loadMenu()
      if (this.orderId) this.loadOrder()
    },
    fmt(v) {
      return Number(v || 0).toFixed(2)
    },
    imgUrl(imgName) {
      return getFileAccessHttpUrl('systemConfig/static/' + String(imgName).split(',')[0])
    },
    isSoldOut(g) {
      return String(g.soldOut) === '1'
    },
    statusText(s) {
      return { '1': '待付款/待接单', '2': '就餐中', '3': '已完成', '5': '已取消', '6': '申请退款',
        '7': '退款通过', '8': '退款拒绝', '9': '已拒单', parked: '挂单中' }[s] || s
    },
    statusColor(s) {
      return { '1': 'orange', '2': 'blue', '3': 'green', '5': 'red', '6': 'orange',
        '7': 'red', '8': 'default', '9': 'red', parked: 'purple' }[s] || ''
    },
    optionsText(json) {
      if (!json) return ''
      try {
        const arr = JSON.parse(String(json))
        if (!Array.isArray(arr)) return ''
        return arr.map(o => o.valueName).filter(Boolean).join('/')
      } catch (e) {
        return ''
      }
    },
    loadMenu() {
      if (!this.storeId) return
      this.menuLoading = true
      const params = { storeId: this.storeId, currentPage: 1, pageSize: 200 }
      if (this.activeCategory > 0) params.categoryId = this.activeCategory
      const kw = (this.searchKey || '').trim()
      if (kw) params.search = kw
      menuList(params).then(res => {
        if (res.code === 200 && res.data) {
          if (res.data.categories) this.categories = res.data.categories
          this.goods = res.data.rows || []
        } else {
          this.$message.error(res.message || '菜单查询失败')
        }
      }).catch(() => {}).finally(() => {
        this.menuLoading = false
      })
    },
    switchCategory(id) {
      if (this.activeCategory === id) return
      this.activeCategory = id
      this.loadMenu()
    },
    loadOrder() {
      orderDetail(this.orderId).then(res => {
        if (res.code === 200 && res.data) {
          this.order = res.data
        } else {
          this.$message.error(res.message || '订单查询失败')
        }
      }).catch(() => {})
    },
    onGoodsClick(g) {
      if (this.isSoldOut(g)) {
        this.$message.warning('菜品已估清')
        return
      }
      if ((g.options || []).length > 0) {
        this.modalGoods = g
        this.modalVisible = true
      } else {
        this.addToCart({
          materialId: g.materialId,
          materialName: g.materialName,
          quantity: 1,
          remark: '',
          optionValueIds: [],
          optionText: '',
          unitRef: Number(g.minPrice || 0)
        })
      }
    },
    onModalConfirm(payload) {
      this.modalVisible = false
      this.addToCart({
        materialId: this.modalGoods.materialId,
        materialName: this.modalGoods.materialName,
        quantity: payload.quantity,
        remark: payload.remark,
        optionValueIds: payload.optionValueIds,
        optionText: payload.optionText,
        unitRef: Number(this.modalGoods.minPrice || 0) + payload.extraAmount
      })
    },
    // 同商品+同选项+同备注的行合并数量
    addToCart(line) {
      const key = line.materialId + '|' + [...line.optionValueIds].sort((a, b) => a - b).join(',') + '|' + (line.remark || '')
      const exist = this.cart.find(i => i.key === key)
      if (exist) {
        exist.quantity += line.quantity
      } else {
        this.cart.push(Object.assign({ key: key }, line))
      }
    },
    decQty(idx) {
      const line = this.cart[idx]
      if (line.quantity > 1) {
        line.quantity--
      } else {
        this.cart.splice(idx, 1)
      }
    },
    // 无订单时先开台（堂食 tableId>0 / 外带 0），再统一下单
    ensureOrder() {
      if (this.orderId) return Promise.resolve(this.orderId)
      const body = { storeId: this.storeId, orderType: this.tableId > 0 ? 'dine_in' : 'takeout' }
      if (this.tableId > 0) body.tableId = this.tableId
      return orderCreate(body).then(res => {
        if (res.code === 200 && res.data) {
          this.orderId = res.data.id
          // 路由补写 orderId，刷新不丢上下文
          this.$router.replace({ query: Object.assign({}, this.$route.query, { orderId: this.orderId }) }).catch(() => {})
          return this.orderId
        }
        return Promise.reject(res)
      })
    },
    handleSubmit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.ensureOrder()
        .then(id => orderAddItems(id, {
          items: this.cart.map(line => ({
            materialId: line.materialId,
            quantity: line.quantity,
            optionValueIds: line.optionValueIds,
            remark: line.remark || undefined
          }))
        }))
        .then(res => {
          if (res.code === 200) {
            this.$message.success('下单成功')
            this.cart = []
            this.loadOrder()
          } else {
            this.$message.error(res.message || '下单失败')
          }
        })
        .catch(err => {
          if (err && err.message) {
            this.$message.error(err.message)
          } else {
            this.$message.error('下单失败，请重试')
          }
        })
        .finally(() => {
          this.submitting = false
        })
    },
    // 挂单：仅已下单且无待下单商品（parked 由总览取单恢复）
    handlePark() {
      if (!this.parkable) {
        if (this.cart.length) this.$message.warning('有待下单商品，请先下单再挂单')
        return
      }
      orderPark(this.orderId).then(res => {
        if (res.code === 200) {
          this.$message.success('已挂单')
          this.$router.push('/cashier/dining')
        } else {
          this.$message.error(res.message || '挂单失败')
          this.loadOrder()
        }
      }).catch(() => {
        this.$message.error('挂单失败，请重试')
      })
    },
    handleSettle() {
      if (!this.settleable) return
      this.$router.push(`/cashier/dining/settle/${this.orderId}`)
    },
    handleBack() {
      if (this.cart.length) {
        this.$confirm({
          title: '离开确认',
          content: '有待下单商品，离开后将丢失，确定返回？',
          onOk: () => { this.$router.push('/cashier/dining') }
        })
      } else {
        this.$router.push('/cashier/dining')
      }
    }
  }
}
</script>

<style scoped>
.order-panel {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  overflow: hidden;
}
.top-bar {
  height: 56px;
  flex-shrink: 0;
  background: #001529;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}
.top-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.top-table {
  font-size: 16px;
  font-weight: 600;
}
.top-order-no {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
}
.top-right {
  display: flex;
  align-items: center;
}
.top-store {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  margin-right: 14px;
}
.top-user {
  font-size: 13px;
  margin-right: 14px;
}
.panel-body {
  flex: 1;
  display: flex;
  min-height: 0;
}
.menu-side {
  width: 170px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  overflow-y: auto;
}
.cat-item {
  padding: 12px 14px;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  border-left: 3px solid transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cat-item:hover { color: #0081ff; }
.cat-item.active {
  color: #0081ff;
  background: #f0f8ff;
  border-left-color: #0081ff;
  font-weight: 600;
}
.goods-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px 16px;
}
.goods-search {
  margin-bottom: 12px;
  flex-shrink: 0;
}
.goods-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  align-content: start;
}
.goods-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  user-select: none;
}
.goods-card:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12); }
.goods-card.soldout { opacity: 0.55; cursor: not-allowed; }
.goods-img {
  height: 96px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}
.goods-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.img-holder {
  font-size: 26px;
  color: #ccc;
  font-weight: 600;
}
.goods-info {
  padding: 8px 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.goods-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.goods-desc {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.goods-bottom {
  margin-top: auto;
  padding-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.goods-price {
  color: #e4393c;
  font-weight: 600;
  font-size: 15px;
}
.goods-opt {
  font-size: 11px;
  color: #0081ff;
  border: 1px solid #b3d8ff;
  border-radius: 3px;
  padding: 0 4px;
}
.goods-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}
.cart-side {
  width: 320px;
  flex-shrink: 0;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}
.cart-title {
  padding: 14px 16px 10px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  flex-shrink: 0;
}
.cart-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 0 12px;
}
.cart-group-label {
  font-size: 12px;
  color: #999;
  padding: 6px 4px;
}
.cart-line {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.cart-line.saved { background: #fafafa; }
.line-main { flex: 1; min-width: 0; }
.line-name {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}
.line-sub {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
  word-break: break-all;
}
.line-right {
  text-align: right;
  flex-shrink: 0;
}
.line-qty { font-size: 12px; color: #999; }
.line-amount {
  font-size: 14px;
  color: #e4393c;
  font-weight: 600;
  margin-top: 4px;
}
.qty-step {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.qty-num {
  min-width: 28px;
  text-align: center;
  font-weight: 600;
}
.line-del { margin-left: auto; }
.cart-empty {
  color: #ccc;
  text-align: center;
  padding: 40px 0;
  font-size: 13px;
}
.cart-footer {
  flex-shrink: 0;
  border-top: 1px solid #f0f0f0;
  padding: 10px 16px 14px;
}
.total-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #555;
  margin-bottom: 6px;
}
.total-num {
  color: #e4393c;
  font-weight: 600;
  font-size: 16px;
}
</style>
